
import { debounce } from 'lodash'
import { applicantStore } from '@/store'
import axios, { CancelTokenSource } from 'axios'
import { KeyEvent } from '@/commons/constants/event'
import { Address } from '@/store/entities/application'
import { Component, Vue } from 'vue-property-decorator'
import { Country } from '@/commons/constants/classifier'
import { ApplicationContactAddress } from '@/commons/types'
import { AddressService } from '@/store/services/address.service'

@Component
export default class AdsSearch extends Vue {
    selectedIndex = -1
    addresses: Array<Address> = []
    search = debounce(this.fetchAddresses, 400)
    searchCancelToken: CancelTokenSource | null = null

    private async fetchAddresses (searchString: string): Promise<void> {
        this.resetAddressList()
        this.cancelSearch()
        this.searchCancelToken = axios.CancelToken.source()
        this.addresses = await AddressService.fetchAddresses(searchString, this.searchCancelToken)
        if (this.addresses.length > 0) {
            this.$nextTick(() =>
                this.resizeAddressList()
            )
        }
    }

    mounted (): void {
        this.resizeAddressList()
    }

    destroyed (): void {
        this.cancelSearch()
    }

    cancelSearch (): void {
        if (this.searchCancelToken) {
            this.searchCancelToken.cancel()
        }
    }

    get addressText (): string {
        return this.address.fullAddress ? this.address.fullAddress : ''
    }

    set addressText (value) {
        this.setAddress(null, '', value, null)
    }

    get address (): ApplicationContactAddress {
        return applicantStore.applicationContact.address
    }

    get isAddressListVisible (): boolean {
        return this.addresses.length > 0
    }

    async setAddress (adsId: number | null, adsCode: string, fullAddress: string, postalCode: number | null): Promise<void> {
        this.address.country = Country.ESTONIA
        this.address.fullAddress = fullAddress
        this.address.adsAdrId = adsId ? adsId.toString() : null
        this.address.adsOid = adsCode || null
        this.address.postalCode = postalCode ? postalCode.toString() : null
        if (!this.address.postalCode && this.address.adsAdrId) {
            this.searchCancelToken = axios.CancelToken.source()
            this.address.postalCode = await AddressService.fetchPostalCode(this.address.adsAdrId, this.searchCancelToken)
        }
        this.resizeTextArea()
    }

    handleInput (event: Event): void {
        const input: string = (event.target as HTMLInputElement).value.replace(/\s-\s|\s-|-\s/, '-')
        if (input.length < 3) {
            this.resetAddressList()
            return
        }
        this.search(input)
    }

    resizeTextArea (): void {
        const textArea: HTMLElement = this.$refs.addressTextArea as HTMLElement
        const addressList: HTMLElement = this.$refs.addressList as HTMLElement
        textArea.style.height = `0px` // so that scrollHeight would reset
        this.$nextTick(() => {
            textArea.style.height = `${textArea.scrollHeight + 2}px`
        })
        if (addressList) {
            addressList.style.marginTop = `${textArea.scrollHeight + 38}px`
        }
    }

    resizeAddressList (): void {
        this.resizeTextArea()
        const textAreaWidth = (this.$refs.addressTextArea as HTMLElement).clientWidth
        const addressList: HTMLElement = (this.$refs.addressList as HTMLElement)
        if (addressList) {
            addressList.style.width = `${textAreaWidth}px`
        }
    }

    resetAddressList (): void {
        this.addresses = []
        this.selectedIndex = -1
    }

    selectAddress (address: Address): void {
        if (address && address.adsId) {
            this.setAddress(address.adsId, address.adsCode, address.fullAddress, address.postalCode)
        }
        this.resetAddressList()
    }

    moveSelection (eventKey: string): void {
        const UP_KEYS: Array<string> = [KeyEvent.ARROWUP, KeyEvent.UP]
        if (this.isAddressListVisible) {
            UP_KEYS.indexOf(eventKey) > -1 ? --this.selectedIndex : ++this.selectedIndex
            if (this.selectedIndex > this.addresses.length - 1) {
                this.selectedIndex = 0
            } else if (this.selectedIndex < 0) {
                this.selectedIndex = this.addresses.length - 1
            }
        }
    }

    onKeyUp (event: KeyboardEvent): void {
        const keyEvent = event.key
        const navigationKeys: Array<string> = [
            KeyEvent.ARROWUP,
            KeyEvent.UP,
            KeyEvent.ARROWDOWN,
            KeyEvent.DOWN
        ]
        if (navigationKeys.indexOf(keyEvent) > -1) {
            this.moveSelection(keyEvent)
        }
        if (keyEvent === KeyEvent.ENTER && this.selectedIndex > -1) {
            this.selectAddress(this.addresses[this.selectedIndex])
        }
    }

    onKeyDown (event: KeyboardEvent): void {
        const keyEvent = event.key
        if (keyEvent === KeyEvent.TAB) {
            this.resetAddressList()
        } else if (keyEvent === KeyEvent.ENTER && this.selectedIndex > -1) {
            event.preventDefault()
        }
    }

    handleBlur (event: Event) {
        this.$emit('blur', event)
    }
}

